type ConversationSummaryLike = {
    readonly id: string;
};

type ResolveSelectedConversationIdParams = {
    readonly fixedConversationId: string | null;
    readonly isCalloutMode: boolean;
    readonly requestedConversationId: string | null;
    readonly selectedConversationId: string | null;
    /**
     * A conversation the user explicitly asked for (deep link, notification,
     * sidebar session) and that has already been applied to the selection.
     * It stays selected even when absent from `conversations`.
     */
    readonly pinnedConversationId: string | null;
    /**
     * Conversations whose detail fetch already came back "not found" (e.g. a
     * deleted run still present in a cached list). The fallback must never
     * auto-select one: the not-found recovery clears the selection, so
     * re-picking the same id would ping-pong the two effects until React
     * throws "Maximum update depth exceeded".
     */
    readonly notFoundConversationIds: ReadonlySet<string>;
    readonly conversations: Array<ConversationSummaryLike> | null | undefined;
};

type ResolveSelectedConversationIdResult = {
    readonly nextSelectedConversationId: string;
    readonly shouldClearRequestedConversationId: boolean;
};

export function resolveSelectedConversationId({
    fixedConversationId,
    isCalloutMode,
    requestedConversationId,
    selectedConversationId,
    pinnedConversationId,
    notFoundConversationIds,
    conversations,
}: ResolveSelectedConversationIdParams): ResolveSelectedConversationIdResult | null {
    if (fixedConversationId) {
        return {
            nextSelectedConversationId: fixedConversationId,
            shouldClearRequestedConversationId: false,
        };
    }

    if (requestedConversationId) {
        if (requestedConversationId === selectedConversationId) {
            return null;
        }

        return {
            nextSelectedConversationId: requestedConversationId,
            shouldClearRequestedConversationId: !isCalloutMode,
        };
    }

    if (isCalloutMode) {
        return null;
    }

    if (!conversations?.length) {
        return null;
    }

    // The list drops general chats after 12h of inactivity, but those runs
    // still load by id. Absence from the list therefore no longer means the
    // chat is gone, so an explicitly-opened chat holds the selection instead
    // of being snapped away to the newest listed row.
    if (
        pinnedConversationId !== null &&
        pinnedConversationId === selectedConversationId
    ) {
        return null;
    }

    const selectedConversationStillExists = conversations.some(
        (conversation) => conversation.id === selectedConversationId,
    );
    if (selectedConversationStillExists) {
        return null;
    }

    const nextSelectedConversationId = conversations.find(
        (conversation) => !notFoundConversationIds.has(conversation.id),
    )?.id;
    if (!nextSelectedConversationId) {
        return null;
    }

    return {
        nextSelectedConversationId,
        shouldClearRequestedConversationId: false,
    };
}
